import { useEffect, useState } from 'react';
import Fuse from 'fuse.js';
import { Options } from 'http-proxy-middleware';

export interface ITreePatternOptions {
   cascade?: boolean;
   fuzzySearchOptions?: any;
   fuzzySearchKeys?: any;
}

const defaultOptions: ITreePatternOptions = {
   cascade: false,
   fuzzySearchOptions: null,
   fuzzySearchKeys: null
};

export interface INode<T> {
   key: string;
   value: T;
   children?: INode<T>[];
   parent?: string;
}

export interface IRoot<T> {
   key: string;
   children: INode<T>[];
}

export function isRoot(object: any): object is IRoot<any> {
   return !('value' in object);
}

// We utilize root when the tree presented will always start at the root node.
// TODO: Casecade option. When we need to utilize checkboxes, we need to be able to cascade the selection to all children.
const useTreePattern = <T>(
   root: IRoot<T> | INode<T>,
   options: ITreePatternOptions = defaultOptions
): {
   currentNode: INode<T> | IRoot<T>;
   findNode: (key: string) => [INode<T> | undefined, string | undefined];
   findNodesByKey: (searchTerm: string) => INode<T>[];
   findNodesFuzzy: (searchTerm: string) => INode<T>[];
   selectNode: (key: string) => void;
   toParentNode: () => void;
   toRoot: () => void;
} => {
   const { cascade, fuzzySearchOptions, fuzzySearchKeys } = options;
   const [currentNode, setCurrentNode] = useState<INode<T> | IRoot<T>>(root);
   useEffect(() => {
      setCurrentNode(root);
   }, [root]);

   const flattenRoot = (root: IRoot<T> | INode<T>): INode<T>[] => {
      var queue = isRoot(root) ? [...root.children] : [root];
      var node: INode<T> | undefined = undefined;
      var flatList: INode<T>[] = [];

      while (queue.length > 0) {
         node = queue.shift();
         flatList.push(node);
         if (node.children) queue = queue.concat(node.children);
      }

      return flatList;
   };

   const flatSearchList: INode<T>[] = flattenRoot(root);

   /** Returns [node: INode<T> | undefined, parent: string | undefined] */
   const findNode = (key: string): [INode<T> | undefined, string | undefined] => {
      var queue = isRoot(root) ? [...root.children] : [root];
      var node: INode<T> | undefined = undefined;

      while (queue.length > 0) {
         node = queue.shift();
         if (node.key === key) return [node, node.parent];
         else if (node.children) queue = queue.concat(node.children);
      }

      return [null, null];
   };

   const findNodesFuzzy = (searchTerm: string): INode<T>[] => {
      const options = fuzzySearchOptions
         ? fuzzySearchOptions
         : {
              minMatchCharLength: 2,
              includeScore: true,
              keys: fuzzySearchKeys ? fuzzySearchKeys : ['key']
           };

      const fuse = new Fuse<INode<T>>(flatSearchList, options);

      return fuse
         .search(searchTerm)
         .sort((a, b) => a.score - b.score)
         .map(result => result.item);
   };

   const findNodesByKey = (searchTerm: string): INode<T>[] => {
      if (!searchTerm || searchTerm === '') return [];
      return flatSearchList.filter(node => node.key.match(new RegExp(searchTerm.replaceAll('\\', '\\\\'), 'gi')));
   };

   const selectNode = (key: string): void => {
      const [node, _] = findNode(key);
      if (node) setCurrentNode(node);
   };

   const toParentNode = (): void => {
      if (isRoot(currentNode) || !currentNode.parent) return;
      else if (currentNode.parent === root.key) setCurrentNode(root);
      else {
         const [node, _] = findNode(currentNode.parent);
         if (node) setCurrentNode(node);
      }
   };

   const toRoot = (): void => {
      if (isRoot(currentNode)) return;
      else setCurrentNode(root);
   };

   return {
      currentNode,
      findNode,
      findNodesByKey,
      findNodesFuzzy,
      selectNode,
      toParentNode,
      toRoot
   };
};

export { useTreePattern };
